export type WindowKey = "mtd" | "last_month" | "30d" | "90d";

export type CostsResponse = {
  generated_at: string;
  workspace: {
    id: string;
    workspace_label: string | null;
    display_name: string | null;
    account_tier: string | null;
    owner_email: string | null;
    owner_user_id: string;
  };
  window: WindowKey;
  window_start: string;
  window_end: string;
  baseline: {
    db_monthly_usd: number;
    egress_monthly_usd: number;
    invocations_monthly_usd: number;
    storage_per_gb_month_usd: number;
    window_month_fraction: number;
  };
  ai: {
    total_usd: number;
    events: number;
    failovers: number;
    breakdown: Array<{
      provider: string;
      model: string | null;
      job_kind: string;
      events: number;
      tokens_in: number;
      tokens_out: number;
      cost_usd: number;
    }>;
    daily: Array<{ date: string; cost_usd: number }>;
    source: string;
  };
  storage: {
    total_bytes: number;
    total_gb: number;
    total_objects: number;
    estimated_usd: number;
    by_bucket: Array<{ bucket: string; objects: number; bytes: number; estimated_usd: number }>;
    source: string;
  };
  database: {
    workspace_rows: number;
    total_rows: number;
    share: number;
    estimated_usd: number;
    row_counts: Array<{ table: string; workspace_rows: number; total_rows: number }>;
    source: string;
  };
  egress: { estimated_usd: number; monthly_baseline_usd: number; source: string };
  invocations: { estimated_usd: number; monthly_baseline_usd: number; source: string };
  revenue: {
    total_usd: number;
    invoices: Array<{
      id: string;
      created: string | null;
      amount_paid_usd: number;
      currency: string;
      status: string | null;
    }>;
    stripe_unavailable_reason: string | null;
    source: string;
  };
  total_cost_usd: number;
  net_contribution_usd: number;
};

export const fmtUsd = (n: number) =>
  n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

export const fmtBytes = (b: number) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(2)} MB`;
  return `${(b / 1024 ** 3).toFixed(3)} GB`;
};

const csvCell = (v: unknown): string => {
  let s = v === null || v === undefined ? "" : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function buildCostsCsv(data: CostsResponse): string {
  const lines: string[] = [];
  lines.push("section,key,value");
  lines.push(
    `meta,workspace,${csvCell(data.workspace.workspace_label ?? data.workspace.display_name ?? data.workspace.id)}`,
  );
  lines.push(`meta,owner_email,${csvCell(data.workspace.owner_email)}`);
  lines.push(`meta,window,${data.window}`);
  lines.push(`meta,window_start,${data.window_start}`);
  lines.push(`meta,window_end,${data.window_end}`);
  lines.push(`totals,ai_usd,${data.ai.total_usd}`);
  lines.push(`totals,storage_usd,${data.storage.estimated_usd}`);
  lines.push(`totals,db_usd,${data.database.estimated_usd}`);
  lines.push(`totals,total_cost_usd,${data.total_cost_usd}`);
  lines.push(`totals,revenue_usd,${data.revenue.total_usd}`);
  lines.push(`totals,net_contribution_usd,${data.net_contribution_usd}`);
  for (const r of data.ai.breakdown) {
    lines.push(`ai,${csvCell(`${r.provider}|${r.model ?? ""}|${r.job_kind}`)},${csvCell(r.cost_usd)}`);
  }
  for (const b of data.storage.by_bucket) {
    lines.push(`storage,${csvCell(`${b.bucket}_bytes`)},${csvCell(b.bytes)}`);
  }
  for (const r of data.database.row_counts) {
    lines.push(`db_rows,${csvCell(r.table)},${csvCell(`${r.workspace_rows}/${r.total_rows}`)}`);
  }
  return lines.join("\n");
}
